import { Box, Tab, Tabs, Typography } from "@mui/material";
import _ from "lodash";
import { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import HistogramTabs from "@/components/membership/Preview/HistogramTabs";
import {
  ComparisonTable,
  Errors,
  FieldsChanged,
  LookupTableWarnings,
  Warnings,
} from "@/components/membership/Preview/summaryComponents";
import TemplateMapping from "@/components/membership/Template/TemplateMapping";
import ErrorFallback from "@/components/sharedComponents/ErrorFallback";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const SummaryTabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      sx={{ backgroundColor: "var(--background-grey)" }}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default function SummaryTabs({ summary, mapText }) {
  const [value, setValue] = useState(0);

  const handleChange = (e, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab
            label="Errors & Warnings"
            data-testid="errors-warnings"
            {...a11yProps(0)}
          />
          <Tab
            label="Compare to last file"
            data-testid="comparisons"
            {...a11yProps(1)}
            disabled={summary.use === "catalist-only"}
          />
          <Tab
            label="Template Mapping"
            data-testid="mapping"
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>

      {/* ERRORS & WARNINGS */}
      <SummaryTabPanel value={value} index={0}>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <ErrorFallback error={error} redirect="/" />
          )}
        >
          {summary.confirmation_problems.length === 0 &&
          summary.conversion_problems.length === 0 &&
          _.isEmpty(summary.lookup_problems) ? (
            <Box>No errors or warnings for this file 😀</Box>
          ) : (
            <>
              {summary.confirmation_problems.length > 0 && (
                <Errors errors={summary.confirmation_problems} />
              )}
              {summary.conversion_problems.length > 0 && (
                <Warnings warnings={summary.conversion_problems} />
              )}
              {summary.lookup_problems && (
                <LookupTableWarnings problems={summary.lookup_problems} />
              )}
            </>
          )}
        </ErrorBoundary>
      </SummaryTabPanel>

      {/* COMPARISON TO LAST FILE */}
      <SummaryTabPanel value={value} index={1}>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <ErrorFallback error={error} redirect="/" />
          )}
        >
          {!summary.record_count_changes ? (
            <Box>No previous file to compare</Box>
          ) : (
            <>
              <ComparisonTable summary={summary} />

              <Box sx={{ marginTop: "15px" }}>
                <FieldsChanged
                  title={"Fields added"}
                  fields={summary.fields_added}
                />
                <FieldsChanged
                  title={"Fields removed"}
                  fields={summary.fields_removed}
                />
              </Box>

              <HistogramTabs
                fieldData={summary.field_data}
                data-testid="preview-tabs"
              />
            </>
          )}
        </ErrorBoundary>
      </SummaryTabPanel>

      {/* TEMPLATE MAPPING */}
      <SummaryTabPanel value={value} index={2}>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <ErrorFallback error={error} redirect="/" />
          )}
        >
          <TemplateMapping data={mapText} />
        </ErrorBoundary>
      </SummaryTabPanel>
    </Box>
  );
}
